import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { RouteUrl } from "@/configs/router/routes";
import { useSelectedTournament } from "@/context/tournamentContext";
import { useSelectedTeam } from "@/context/teamContext";
import { useQuery } from "@tanstack/react-query";
import { tournamentTeamsListQueryOptions, tournamentsQueryOptions, type TeamListItem } from "@/api/tournaments";
import { TeamsHeader } from "@/components/general/Teams/TeamsHeader";
import { TeamsSort, type TeamsSortOption, type SortDirection } from "@/components/general/Teams/TeamsSort";
import { useState, useEffect, useMemo } from "react";
import { getTeamLogo, getPointsBadge } from "@/lib/utils";
import { Star } from "lucide-react";
import { Loader } from "@/components/general/common/loader";

const TeamCard = ({ team, isTop, onClick }: { team: TeamListItem; isTop: boolean; onClick: () => void }) => {
    const logo = getTeamLogo(team.teamName, isTop ? "light" : "dark");
    const { bg: kpiBg, text: kpiText, border: kpiBorder } = getPointsBadge(team.avgTeamKpi);

    if (isTop) {
        return (
            <div
                onClick={onClick}
                className="bg-primary text-white rounded-lg shadow-lg border-l-4 border-secondary-token overflow-hidden group hover:shadow-xl transition-all duration-300 relative cursor-pointer"
            >
                <div className="p-3 flex items-center gap-3 md:hidden">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 border border-white/20 shrink-0 flex items-center justify-center">
                        {logo ? (
                            <img
                                src={logo}
                                alt={`${team.teamName} logo`}
                                className="w-full h-full object-contain p-1"
                            />
                        ) : (
                            <span className="text-lg font-black text-white">{team.teamName.charAt(0)}</span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold font-headline leading-tight truncate text-white">{team.teamName}</h3>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                        <Star className="w-4 h-4 text-secondary fill-secondary" />
                        <div
                            className={`inline-flex items-center px-2 py-0.5 ${kpiBg} ${kpiText} rounded font-bold text-sm border ${kpiBorder}`}
                        >
                            {team.avgTeamKpi.toFixed(1)}
                        </div>
                    </div>
                </div>

                <div className="p-6 hidden md:block">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-white/10 border border-white/20 flex-shrink-0 flex items-center justify-center">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt={`${team.teamName} logo`}
                                    className="w-full h-full object-contain p-1 group-hover:scale-110 transition-transform duration-500"
                                />
                            ) : (
                                <span className="text-2xl font-black text-white">{team.teamName.charAt(0)}</span>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5">
                                <Star className="w-4 h-4 text-secondary fill-secondary" />
                                <div
                                    className={`inline-flex items-center px-2.5 py-1 ${kpiBg} ${kpiText} rounded font-bold text-xl border ${kpiBorder}`}
                                >
                                    {team.avgTeamKpi.toFixed(1)}
                                </div>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-white/70">
                                KPI Score
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white font-headline leading-tight">{team.teamName}</h3>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-3">
                        {[
                            { label: "Total KPI", value: team.totalKpi, useTier: false },
                            { label: "Player KPI", value: team.avgPlayerKpi, useTier: true },
                            { label: "Goalie KPI", value: team.avgGoalieKpi, useTier: true },
                        ].map((stat) => {
                            const tier = stat.useTier
                                ? getPointsBadge(stat.value)
                                : {
                                      bg: "bg-surface-container-highest",
                                      text: "text-on-surface",
                                      border: "border-outline-variant/20",
                                  };
                            return (
                                <div key={stat.label}>
                                    <div className="text-[10px] uppercase font-bold text-white/40 mb-1">
                                        {stat.label}
                                    </div>
                                    <div
                                        className={`inline-flex items-center px-1.5 py-0.5 ${tier.bg} ${tier.text} rounded font-bold text-sm border ${tier.border}`}
                                    >
                                        {stat.value.toFixed(1)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={onClick}
            className="bg-white rounded-lg shadow-sm border-l-4 border-primary overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer"
        >
            <div className="p-3 flex items-center gap-3 md:hidden">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface-container-low border border-outline-variant/20 flex-shrink-0 flex items-center justify-center">
                    {logo ? (
                        <img src={logo} alt={`${team.teamName} logo`} className="w-full h-full object-contain p-1" />
                    ) : (
                        <span className="text-lg font-black text-primary">{team.teamName.charAt(0)}</span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold font-headline leading-tight truncate text-primary">{team.teamName}</h3>
                </div>
                <div className="shrink-0">
                    <div
                        className={`inline-flex items-center px-2 py-0.5 ${kpiBg} ${kpiText} rounded font-bold text-sm border ${kpiBorder}`}
                    >
                        {team.avgTeamKpi.toFixed(1)}
                    </div>
                </div>
            </div>

            <div className="p-6 hidden md:block">
                <div className="flex justify-between items-start mb-6">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-surface-container-low border border-outline-variant/20 flex-shrink-0 flex items-center justify-center">
                        {logo ? (
                            <img
                                src={logo}
                                alt={`${team.teamName} logo`}
                                className="w-full h-full object-contain p-1 group-hover:scale-110 transition-transform duration-500"
                            />
                        ) : (
                            <span className="text-2xl font-black text-primary">{team.teamName.charAt(0)}</span>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <div
                            className={`inline-flex items-center px-2.5 py-1 ${kpiBg} ${kpiText} rounded font-bold text-xl border ${kpiBorder}`}
                        >
                            {team.avgTeamKpi.toFixed(1)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mt-1">
                            KPI Score
                        </div>
                    </div>
                </div>
                <div>
                    <h3 className="text-xl font-bold text-primary font-headline leading-tight">{team.teamName}</h3>
                </div>
                <div className="mt-6 pt-6 border-t border-surface-container-highest grid grid-cols-3 gap-3">
                    {[
                        { label: "Total KPI", value: team.totalKpi, useTier: false },
                        { label: "Player KPI", value: team.avgPlayerKpi, useTier: true },
                        { label: "Goalie KPI", value: team.avgGoalieKpi, useTier: true },
                    ].map((stat) => {
                        const tier = stat.useTier
                            ? getPointsBadge(stat.value)
                            : {
                                  bg: "bg-surface-container-highest",
                                  text: "text-on-surface",
                                  border: "border-outline-variant/20",
                              };
                        return (
                            <div key={stat.label}>
                                <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">{stat.label}</div>
                                <div
                                    className={`inline-flex items-center px-1.5 py-0.5 ${tier.bg} ${tier.text} rounded font-bold text-sm border ${tier.border}`}
                                >
                                    {stat.value.toFixed(1)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const TeamsPage = () => {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const navigate = useNavigate();
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const { teamId, setTeam } = useSelectedTeam();
    const [selectedTournament, setSelectedTournament] = useState("");

    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));

    const mostRecentTournament = tournaments
        ?.filter((t) => t.matches && t.matches.length > 0)
        ?.reduce(
            (latest, current) => (latest == undefined || current.id > latest.id ? current : latest),
            undefined as (typeof tournaments)[0] | undefined
        );

    useEffect(() => {
        if (globalTournamentId && tournaments) {
            const globalTournament = tournaments.find((t) => t.id === globalTournamentId);
            if (globalTournament && selectedTournament === "") {
                setSelectedTournament(globalTournament.name);
            }
        } else if (mostRecentTournament && selectedTournament === "") {
            setSelectedTournament(mostRecentTournament.name);
        }
    }, [mostRecentTournament, selectedTournament, globalTournamentId, tournaments]);

    const selectedTournamentId = tournaments?.find((t) => t.name === selectedTournament)?.id;

    const handleTournamentChange = (name: string) => {
        setSelectedTournament(name);
        const tournament = tournaments?.find((t) => t.name === name);
        if (tournament) setGlobalTournamentId(tournament.id);
    };

    useEffect(() => {
        if (selectedTournamentId) setGlobalTournamentId(selectedTournamentId);
    }, [selectedTournamentId, setGlobalTournamentId]);

    const { data: teams, isLoading } = useQuery(tournamentTeamsListQueryOptions(selectedTournamentId));

    const [sortOption, setSortOption] = useState<TeamsSortOption>("avgKpi");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const handleSortChange = (option: TeamsSortOption, direction: SortDirection) => {
        setSortOption(option);
        setSortDirection(direction);
    };

    const sortedTeams = useMemo(() => {
        if (!teams) return [];
        const getValue = (team: TeamListItem) => {
            switch (sortOption) {
                case "totalKpi":
                    return team.totalKpi;
                case "avgKpi":
                    return team.avgTeamKpi;
                case "avgPlayerKpi":
                    return team.avgPlayerKpi;
                case "avgGoalieKpi":
                    return team.avgGoalieKpi;
            }
        };
        return [...teams].sort((a, b) =>
            sortDirection === "desc" ? getValue(b) - getValue(a) : getValue(a) - getValue(b)
        );
    }, [teams, sortOption, sortDirection]);

    const topTeamId = sortedTeams[0]?.teamId;

    if (pathname !== RouteUrl.Teams) {
        return <Outlet />;
    }

    return (
        <main
            data-teams-loaded={!isLoading && !!teams ? "true" : undefined}
            className="flex flex-col p-6 min-h-0 overflow-auto"
        >
            <TeamsHeader
                selectedTournament={selectedTournament}
                onTournamentChange={handleTournamentChange}
                isLoading={isLoading}
                tournaments={tournaments}
            />
            <TeamsSort onSortChange={handleSortChange} />
            {!selectedTournamentId ? (
                <p className="text-sm text-on-surface-variant mt-4">Select a tournament to view teams.</p>
            ) : isLoading ? (
                <div className="py-12 flex justify-center">
                    <Loader message="Loading teams..." />
                </div>
            ) : (
                <div
                    id="onboarding-teams-grid"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-6 pb-6 pt-2"
                >
                    {sortedTeams.map((team) => (
                        <TeamCard
                            key={team.teamId}
                            team={team}
                            isTop={team.teamId === topTeamId}
                            onClick={() => {
                                setTeam(team.teamId, team.teamName);
                                navigate({ to: RouteUrl.Dashboard });
                            }}
                        />
                    ))}
                </div>
            )}
        </main>
    );
};

export default TeamsPage;
